import React, { useState } from 'react';
import { Button, notification } from 'antd';
import { IdcardOutlined } from '@ant-design/icons';
import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';
import { useClient } from '../../common/ClientProvider';
import { useClientConfig } from '../../common/client/ClientConfigProvider';
import { useMemberService } from '../../services/members/MemberServiceProvider';
import { useClientConfigService } from '../../services/clients/ClientConfigServiceProvider';
import { MemberProfile } from '../../model/members/MemberProfile';
import {
  DEFAULT_MEMBERSHIP_CARD_LAYOUT,
  MembershipCardFieldType,
} from '../../model/client/ClientSettings';

interface MembershipCardGeneratorProps {
  member: MemberProfile;
}

// Parse "#RRGGBB" into [r, g, b]. Falls back to the legacy dark-blue header
// colour when the input is not a clean 6-digit hex.
const hexToRgb = (hex: string): [number, number, number] => {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) return [30, 40, 120];
  const value = parseInt(match[1], 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
};

const MembershipCardGenerator: React.FC<MembershipCardGeneratorProps> = ({ member }) => {
  const { currentClientId } = useClient();
  const { branding, membershipCard } = useClientConfig();
  const memberService = useMemberService();
  const clientConfigService = useClientConfigService();
  const [isGenerating, setIsGenerating] = useState(false);

  // Defensive fallback: the API always serves a layout (its own default), but a
  // stale cached config or a test stub may omit it — render the default rather
  // than crash.
  const layout = membershipCard ?? DEFAULT_MEMBERSHIP_CARD_LAYOUT;

  // Resolve a card field's value from the member. companyName lives in
  // member_metadata under the PascalCase backend enum name.
  const fieldValue = (type: MembershipCardFieldType): string => {
    switch (type) {
      case 'memberName':
        return member.name || 'Unknown Member';
      case 'membershipId':
        return member.membershipId || '';
      case 'companyName': {
        const value = member.metadata?.CompanyName;
        return typeof value === 'string' ? value : '';
      }
      default:
        return '';
    }
  };

  // Fetch an image URL and return base64 + jsPDF image format, or null on any
  // failure (empty URL, CORS, non-image). Callers fall back to text.
  const fetchImageForPdf = async (
    url: string
  ): Promise<{ base64: string; format: string } | null> => {
    if (!url) return null;
    try {
      const response = await fetch(url);
      if (!response.ok) return null;
      const blob = await response.blob();
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      });
      const format = blob.type.includes('png')
        ? 'PNG'
        : blob.type.includes('gif')
          ? 'GIF'
          : 'JPEG';
      return { base64, format };
    } catch (error) {
      console.warn('Failed to fetch image for membership card:', error);
      return null;
    }
  };

  const generateMembershipCard = async () => {
    setIsGenerating(true);
    try {
      // Fetch the photo as a base64 data URL from our API rather than using the
      // S3 presigned URL directly. A direct browser fetch to the presigned URL
      // is blocked by S3's CORS policy; proxying the bytes through the backend
      // avoids the cross-origin call entirely. Only fetch when the layout shows
      // a photo. getMemberPhotoData already returns '' on failure.
      const photoUrl = layout.showPhoto
        ? await memberService.getMemberPhotoData(member.membershipId)
        : '';

      // Create new PDF document with credit card dimensions (85.6mm x 53.98mm)
      // Convert to points (1mm = 2.83465 points)
      const cardWidth = 85.6 * 2.83465; // ~242.6 points
      const cardHeight = 53.98 * 2.83465; // ~153 points

      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: [cardWidth, cardHeight]
      });

      // Set background color (white)
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, cardWidth, cardHeight, 'F');

      // Add main border
      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(1);
      pdf.rect(1, 1, cardWidth - 2, cardHeight - 2);

      // Header section with club branding - full width
      const headerHeight = 40;

      // Header background — header.backgroundColor override, else branding.membershipCardBg.
      const [headerR, headerG, headerB] = hexToRgb(layout.header.backgroundColor ?? branding.membershipCardBg);
      pdf.setFillColor(headerR, headerG, headerB);
      pdf.rect(5, 5, cardWidth - 10, headerHeight, 'F');

      // Header branches on layout.header.type: a centred logo image, or the
      // club name + tagline text. Logo mode falls back to the text header if the
      // logo is empty or fails to load, so the header is never blank.
      //
      // The card logo comes from membershipCard header.logoKey (private assets
      // bucket), streamed as a base64 data URL by the API — a browser fetch of a
      // presigned URL would be blocked by S3 CORS (same reason the photo is
      // proxied above). This is card-specific and independent of branding.logoUrl.
      let headerRendered = false;
      if (layout.header.type === 'logo') {
        // Only hit the API when a logo key is actually configured; the server is
        // the source of truth for the bytes, but skipping the call for an empty
        // key avoids a round-trip that would just return ''.
        const logoData = layout.header.logoKey
          ? await clientConfigService.getMembershipCardLogoData()
          : '';
        const logo = logoData ? await fetchImageForPdf(logoData) : null;
        if (logo) {
          // Scale the logo to fit the header band by its own aspect ratio so it
          // is never distorted: a single uniform scale (the smaller of the
          // width- and height-fit ratios) is height-constrained in the common
          // case and width-constrained only for extremely wide logos — either
          // way the proportions always hold. getImageProperties reads the
          // image's intrinsic pixel dimensions; if it throws or reports no
          // dimensions we fall back to the centred square below.
          let naturalW = 0;
          let naturalH = 0;
          try {
            const props = pdf.getImageProperties(logo.base64);
            naturalW = props?.width ?? 0;
            naturalH = props?.height ?? 0;
          } catch {
            // Unparseable image metadata — leave dimensions at 0 to trigger the
            // square fallback rather than failing the whole card.
          }

          // Available header area: full band width (matches the rect drawn at
          // x=5, w=cardWidth-10) with a little vertical breathing room.
          const maxLogoWidth = cardWidth - 10;
          const maxLogoHeight = headerHeight - 6;

          if (naturalW > 0 && naturalH > 0) {
            const scale = Math.min(maxLogoWidth / naturalW, maxLogoHeight / naturalH);
            const logoWidth = naturalW * scale;
            const logoHeight = naturalH * scale;
            const logoX = (cardWidth - logoWidth) / 2; // centre horizontally
            const logoY = 5 + (headerHeight - logoHeight) / 2; // centre in the band
            pdf.addImage(logo.base64, logo.format, logoX, logoY, logoWidth, logoHeight);
          } else {
            // Fallback if intrinsic dimensions are unavailable: centred square.
            const logoSize = headerHeight - 8;
            pdf.addImage(logo.base64, logo.format, (cardWidth - logoSize) / 2, 9, logoSize, logoSize);
          }
          headerRendered = true;
        }
      }

      if (!headerRendered) {
        // Text header. In text mode an optional small logo sits on the left
        // (skipped silently if empty); the club name is centred with the tagline
        // below. No hardcoded fallback: if both name sources are empty the
        // header is left untitled rather than leaking another tenant's name.
        if (layout.header.type === 'text') {
          const logo = await fetchImageForPdf(branding.logoUrl);
          if (logo) {
            const logoSize = headerHeight - 8;
            pdf.addImage(logo.base64, logo.format, 9, 9, logoSize, logoSize);
          }
        }

        const clubName = (branding.clubName || currentClientId || '').toUpperCase();
        if (clubName) {
          pdf.setFontSize(16);
          pdf.setTextColor(255, 255, 255);
          pdf.setFont('helvetica', 'bold');
          pdf.text(clubName, cardWidth / 2, 25, { align: 'center' });
        }

        if (branding.tagline) {
          pdf.setFontSize(8);
          pdf.setTextColor(255, 255, 255);
          pdf.setFont('helvetica', 'normal');
          pdf.text(branding.tagline, cardWidth / 2, 38, { align: 'center' });
        }
      }

      // Main content area
      const contentStartY = headerHeight + 10;
      const contentHeight = cardHeight - contentStartY - 35; // Reserve space for barcode

      // Right content area (photo) — only reserved when the layout shows a photo.
      const photoAreaWidth = layout.showPhoto ? cardWidth * 0.35 : 0;
      const photoAreaX = cardWidth - photoAreaWidth - 8;

      // Left content area (member details). With no photo the text uses the full
      // card width; otherwise it stops short of the photo column.
      const leftContentX = 8;
      const leftContentWidth = (layout.showPhoto ? photoAreaX : cardWidth - 8) - 16;
      const fieldMaxWidth = leftContentWidth - 10;

      // Function to calculate optimal font size for text to fit within width
      const getOptimalFontSize = (text: string, maxWidth: number, maxFontSize: number, minFontSize: number = 8) => {
        let fontSize = maxFontSize;
        pdf.setFontSize(fontSize);

        while (fontSize > minFontSize) {
          const textWidth = pdf.getTextWidth(text);
          if (textWidth <= maxWidth) {
            return fontSize;
          }
          fontSize -= 0.5;
          pdf.setFontSize(fontSize);
        }
        return minFontSize;
      };

      // Render the configured fields top-to-bottom. memberName is the bold
      // headline; the rest render as an optional grey label followed by the
      // bold value. hideWhenEmpty drops a field (and its label) when the member
      // has no value, so an empty company never leaves a dangling "Company:".
      let cursorY = contentStartY + 25;
      for (const field of layout.fields) {
        const value = fieldValue(field.type);
        if (field.hideWhenEmpty && !value) continue;

        if (field.type === 'memberName') {
          pdf.setFont('helvetica', 'bold');
          const fontSize = getOptimalFontSize(value, fieldMaxWidth, 16, 10);
          pdf.setFontSize(fontSize);
          pdf.setTextColor(0, 0, 0);
          pdf.text(value, leftContentX, cursorY);
          cursorY += fontSize * 1.5;
          continue;
        }

        if (field.label) {
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.setFont('helvetica', 'normal');
          pdf.text(field.label, leftContentX, cursorY);

          const labelWidth = pdf.getTextWidth(field.label);
          pdf.setFont('helvetica', 'bold');
          const fontSize = getOptimalFontSize(value, fieldMaxWidth - labelWidth - 10, 14, 10);
          pdf.setFontSize(fontSize);
          pdf.setTextColor(0, 0, 0);
          pdf.text(value, leftContentX + labelWidth + 10, cursorY);
          cursorY += Math.max(fontSize, 14) * 1.4;
        } else {
          pdf.setFont('helvetica', 'bold');
          const fontSize = getOptimalFontSize(value, fieldMaxWidth, 14, 10);
          pdf.setFontSize(fontSize);
          pdf.setTextColor(0, 0, 0);
          pdf.text(value, leftContentX, cursorY);
          cursorY += fontSize * 1.4;
        }
      }

      if (layout.showPhoto) {
        // Photo area positioning (after text calculations)
        const photoWidth = photoAreaWidth - 10;
        const photoHeight = contentHeight - 10;
        const photoX = photoAreaX + 5;
        const photoY = contentStartY + 5;

        // Photo background and border
        pdf.setFillColor(240, 240, 240);
        pdf.rect(photoX - 2, photoY - 2, photoWidth + 4, photoHeight + 4, 'F');

        pdf.setDrawColor(180, 180, 180);
        pdf.setLineWidth(1);
        pdf.rect(photoX - 2, photoY - 2, photoWidth + 4, photoHeight + 4);

        // Add photo if available
        if (photoUrl) {
          try {
            const isDataUrl = photoUrl.startsWith('data:');

            let base64Data: string;
            let imageFormat = 'JPEG';

            // Check if the URL is already a data URL
            if (isDataUrl) {
              base64Data = photoUrl.split(',')[1];
              if (photoUrl.includes('image/png')) imageFormat = 'PNG';
              else if (photoUrl.includes('image/gif')) imageFormat = 'GIF';
              else imageFormat = 'JPEG';
            } else {
              // Fetch the photo from URL
              const response = await fetch(photoUrl);

              if (response.ok) {
                const blob = await response.blob();

                // Convert blob to base64 using a Promise-based approach
                base64Data = await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();

                  reader.onload = () => {
                    try {
                      const result = reader.result as string;
                      resolve(result.split(',')[1]);
                    } catch (error) {
                      reject(error);
                    }
                  };

                  reader.onerror = () => reject(reader.error);
                  reader.readAsDataURL(blob);
                });

                // Determine image format from blob type or URL extension
                if (blob.type) {
                  if (blob.type.includes('png')) imageFormat = 'PNG';
                  else if (blob.type.includes('jpeg') || blob.type.includes('jpg')) imageFormat = 'JPEG';
                  else if (blob.type.includes('gif')) imageFormat = 'GIF';
                } else if (photoUrl.toLowerCase().includes('.png')) {
                  imageFormat = 'PNG';
                } else if (photoUrl.toLowerCase().includes('.gif')) {
                  imageFormat = 'GIF';
                }
              } else {
                console.warn('Failed to fetch photo, status:', response.status, response.statusText);
                throw new Error(`Failed to fetch photo: ${response.status} ${response.statusText}`);
              }
            }

            // Add the image to PDF
            pdf.addImage(base64Data, imageFormat, photoX, photoY, photoWidth, photoHeight);

          } catch (error) {
            console.error('Error processing member photo:', error);
            // Add placeholder text if photo fails
            pdf.setFontSize(10);
            pdf.setTextColor(150, 150, 150);
            pdf.setFont('helvetica', 'normal');
            pdf.text('PHOTO', photoX + photoWidth / 2, photoY + photoHeight / 2, { align: 'center' });
          }
        } else {
          // Add placeholder text
          pdf.setFontSize(10);
          pdf.setTextColor(150, 150, 150);
          pdf.setFont('helvetica', 'normal');
          pdf.text('PHOTO', photoX + photoWidth / 2, photoY + photoHeight / 2, { align: 'center' });
        }
      }

      // Bottom barcode section - positioned at the bottom, when enabled.
      if (layout.barcode.show) {
        const barcodeWidth = 160;
        const barcodeHeight = 20;
        const barcodeX = (cardWidth - barcodeWidth) / 2;
        const barcodeY = cardHeight - 30;

        // 'membershipId' is the only supported barcode source today.
        const barcodeValue = member.membershipId;
        generateBarcode(pdf, barcodeValue, layout.barcode.format, barcodeX, barcodeY, barcodeWidth, barcodeHeight);
      }

      pdf.save(`${member.name || 'member'}_membership_card.pdf`);

      notification.success({
        message: 'Success',
        description: 'Membership card PDF generated successfully!',
      });
    } catch (error) {
      console.error('Error generating membership card:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to generate membership card PDF',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const generateBarcode = (
    pdf: jsPDF,
    value: string,
    format: string,
    barcodeX: number,
    barcodeY: number,
    barcodeWidth: number,
    barcodeHeight: number
  ) => {
    try {
      // Create a temporary canvas for the barcode
      const canvas = document.createElement('canvas');

      // Generate barcode using JsBarcode with settings to match the card.
      JsBarcode(canvas, value, {
        format, // configurable; CODE128 today
        width: 2, // Slightly thicker bars to match the appearance
        height: 40, // Taller for better scanning
        displayValue: false, // No text below barcode in card
        background: '#ffffff',
        lineColor: '#000000',
        margin: 2, // Small margin
        fontSize: 0, // No font since displayValue is false
        textPosition: 'bottom'
      });

      // Convert canvas to image and add to PDF
      const barcodeDataUrl = canvas.toDataURL('image/png');
      pdf.addImage(barcodeDataUrl, 'PNG', barcodeX, barcodeY, barcodeWidth, barcodeHeight);

    } catch (error) {
      console.error('Error generating barcode:', error);
      // Fallback: add a simple text representation
      pdf.setFontSize(8);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`ID: ${value}`, barcodeX + barcodeWidth / 2, barcodeY + barcodeHeight / 2, { align: 'center' });
    }
  };

  return (
    <Button
      type="primary"
      icon={<IdcardOutlined />}
      onClick={generateMembershipCard}
      loading={isGenerating}
      disabled={isGenerating}
      data-testid="generate-membership-card-btn"
    >
      Generate Membership Card
    </Button>
  );
};

export default MembershipCardGenerator;
